(function () {
  'use strict';

  if (customElements.get('testimonials-slider')) return;

  const MOBILE_MAX = 767;
  const DESKTOP_ARROWS_MIN = 1024;
  const CAROUSEL_MIN_SLIDES = 5;

  class TestimonialsSlider extends HTMLElement {
    constructor() {
      super();
      this.flkty = null;
      this._navArrowsShown = null;
      this._resizeRaf = null;
      this.onResizeCallback = () => {
        if (this._resizeRaf != null) return;
        this._resizeRaf = requestAnimationFrame(() => {
          this._resizeRaf = null;
          this.onResize();
        });
      };
    }

    connectedCallback() {
      this.container = this.closest('[data-section-type="testimonials"]');
      this.refreshSlides();
      this.checkSlideCount();
      this.listen();
    }

    refreshSlides() {
      this.slides = this.querySelectorAll('[data-item]');
    }

    isScrollMode() {
      return window.innerWidth <= MOBILE_MAX;
    }

    mapCellAlign() {
      return 'center';
    }

    shouldShowNavArrows() {
      return (
        window.innerWidth >= DESKTOP_ARROWS_MIN &&
        this.slides.length >= CAROUSEL_MIN_SLIDES
      );
    }

    needsCarousel() {
      return this.slides.length >= CAROUSEL_MIN_SLIDES;
    }

    onResize() {
      this.refreshSlides();
      const scroll = this.isScrollMode();
      if (scroll) {
        this.setScrollMode(true);
        this.destroySlider();
        return;
      }
      this.setScrollMode(false);

      const showNav = this.shouldShowNavArrows();
      const wantCarousel = !scroll && this.needsCarousel();
      if (this.flkty && !wantCarousel) {
        this.destroySlider();
        this.checkSlideCount();
        return;
      }
      if (
        this.flkty &&
        this._navArrowsShown !== null &&
        this._navArrowsShown !== showNav
      ) {
        this.destroySlider();
        this.checkSlideCount();
        return;
      }

      this.checkSlideCount();
      if (this.flkty) {
        this.flkty.resize();
      }
    }

    setScrollMode(on) {
      this.classList.toggle('testimonials__slider--scroll-mode', on);
      if (this.container) {
        this.container.classList.toggle('testimonials-section--scroll', on);
        this.container.classList.remove('has-arrows');
      }
    }

    checkSlideCount() {
      this.refreshSlides();

      if (this.isScrollMode()) {
        this.setScrollMode(true);
        this.destroySlider();
        return;
      }
      this.setScrollMode(false);

      if (!this.needsCarousel()) {
        this.destroySlider();
        return;
      }

      if (!this.flkty) {
        this.initSlider();
        return;
      }
      if (!this.flkty.isActive) {
        this.initSlider();
        return;
      }
      this.flkty.resize();
    }

    initSlider() {
      if (this.flkty || this.isScrollMode() || !this.needsCarousel()) return;

      this.refreshSlides();
      const useArrows = this.shouldShowNavArrows();
      this._navArrowsShown = useArrows;

      this.flkty = new window.theme.Flickity(this, {
        cellSelector: '[data-item]',
        prevNextButtons: useArrows,
        pageDots: false,
        groupCells: false,
        cellAlign: this.mapCellAlign(),
        contain: true,
        adaptiveHeight: false,
        imagesLoaded: true,
        wrapAround: false,
        on: {
          ready: () => {
            this.slidesTabIndex();
            requestAnimationFrame(() => {
              this.querySelectorAll('.flickity-button').forEach((btn) => {
                window.theme.wrap(btn, 'flickity-button-holder');
              });
              if (this.flkty) this.flkty.resize();
            });
          },
          change: () => this.slidesTabIndex(),
        },
      });

      if (this.container) {
        if (useArrows) this.container.classList.add('has-arrows');
        else this.container.classList.remove('has-arrows');
      }

      this.createResizeClass();
    }

    createResizeClass() {
      if (typeof window.theme.Flickity.prototype._createResizeClass === 'function') return;

      window.theme.Flickity.prototype._createResizeClass = function () {
        this.element.classList.add('carousel--resize');
      };
      window.theme.Flickity.createMethods.push('_createResizeClass');
      const resize = window.theme.Flickity.prototype.resize;
      window.theme.Flickity.prototype.resize = function () {
        this.element.classList.remove('carousel--resize');
        resize.call(this);
        this.element.classList.add('carousel--resize');
      };
    }

    slidesTabIndex() {
      const data = window.theme.Flickity.data(this);
      if (!data) return;
      data.cells.forEach((cell) => {
        const focus = cell.element.classList.contains('is-selected') ? '0' : '-1';
        cell.element.querySelectorAll('a, button').forEach((el) => {
          el.setAttribute('tabindex', focus);
        });
      });
    }

    destroySlider() {
      if (!this.classList.contains('flickity-enabled')) {
        this._navArrowsShown = null;
        return;
      }
      if (this.flkty) {
        this.querySelectorAll('.flickity-button')?.forEach((btn) => {
          const holder = btn.parentNode;
          if (holder?.classList.contains('flickity-button-holder')) {
            holder.parentNode.insertBefore(btn, holder);
            holder.parentNode.removeChild(holder);
          }
        });
        this.flkty.destroy();
        this.flkty = null;
      }
      this._navArrowsShown = null;
      if (this.container) this.container.classList.remove('has-arrows');
    }

    listen() {
      document.addEventListener('theme:resize:width', this.onResizeCallback);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:resize:width', this.onResizeCallback);
      if (this._resizeRaf != null) {
        cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = null;
      }
    }
  }

  customElements.define('testimonials-slider', TestimonialsSlider);
})();
